/**
 * @fileoverview Reusable monitor configuration form component.
 * Provides a comprehensive form for creating and editing API monitors
 * with fields for URL, method, interval, headers, body, and alert
 * conditions. Uses React Hook Form with Zod validation.
 * @module components/MonitorForm
 */

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { insertMonitorSchema, type InsertMonitor } from "@shared/schema";
import { z } from "zod";
import { useState } from "react";

/** Extended form schema with headers as JSON string */
const formSchema = insertMonitorSchema.extend({
  headers: z.string().optional(),
});

/** Form data type inferred from formSchema */
type FormData = z.infer<typeof formSchema>;

/** Available check interval options with display labels */
const INTERVAL_OPTIONS = [
  { value: "60000", label: "1 minute" },
  { value: "300000", label: "5 minutes" },
  { value: "900000", label: "15 minutes" },
  { value: "1800000", label: "30 minutes" },
  { value: "3600000", label: "1 hour" },
];

/** Common HTTP status codes for alert configuration */
const COMMON_STATUS_CODES = [
  { code: 400, label: "400 Bad Request" },
  { code: 401, label: "401 Unauthorized" },
  { code: 403, label: "403 Forbidden" },
  { code: 404, label: "404 Not Found" },
  { code: 408, label: "408 Request Timeout" },
  { code: 429, label: "429 Too Many Requests" },
  { code: 500, label: "500 Internal Server Error" },
  { code: 502, label: "502 Bad Gateway" },
  { code: 503, label: "503 Service Unavailable" },
  { code: 504, label: "504 Gateway Timeout" },
];

/**
 * Props for MonitorForm component.
 */
interface MonitorFormProps {
  /** Initial form values for editing existing monitor */
  initialValues?: Partial<FormData>;
  /** Form submission handler */
  onSubmit: (data: FormData) => void;
  /** Custom submit button label */
  submitLabel?: string;
  /** Whether form submission is in progress */
  isPending?: boolean;
  /** Optional cancel handler (shows cancel button if provided) */
  onCancel?: () => void;
}

/**
 * Monitor configuration form component.
 * Renders a comprehensive form with:
 * - Name and URL fields
 * - Interval and HTTP method selects
 * - Alert status code checkboxes
 * - Collapsible advanced options (headers, body)
 * - Active/inactive toggle
 * @param props - Component props
 * @returns Form component JSX
 */
export function MonitorForm({
  initialValues,
  onSubmit,
  submitLabel = "Start Monitoring",
  isPending = false,
  onCancel,
}: MonitorFormProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: initialValues?.name || "",
      url: initialValues?.url || "",
      method: initialValues?.method || "GET",
      interval: initialValues?.interval || 300000,
      headers: initialValues?.headers ? JSON.stringify(initialValues.headers, null, 2) : "",
      body: initialValues?.body || "",
      alertStatusCodes: initialValues?.alertStatusCodes || [],
      isActive: initialValues?.isActive ?? true,
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" data-testid="monitor-form">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem data-testid="monitor-form-name-item">
              <FormLabel data-testid="monitor-form-name-label">Monitor Name</FormLabel>
              <FormControl>
                <Input
                  placeholder="My API Endpoint"
                  {...field}
                  data-testid="monitor-form-name-input"
                />
              </FormControl>
              <FormDescription data-testid="monitor-form-name-description">
                A friendly name to identify this monitor
              </FormDescription>
              <FormMessage data-testid="monitor-form-name-error" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="url"
          render={({ field }) => (
            <FormItem data-testid="monitor-form-url-item">
              <FormLabel data-testid="monitor-form-url-label">URL</FormLabel>
              <FormControl>
                <Input
                  placeholder="https://api.example.com/health"
                  className="font-mono text-sm"
                  {...field}
                  data-testid="monitor-form-url-input"
                />
              </FormControl>
              <FormDescription data-testid="monitor-form-url-description">
                The endpoint URL you want to monitor
              </FormDescription>
              <FormMessage data-testid="monitor-form-url-error" />
            </FormItem>
          )}
        />

        <div className="grid gap-6 md:grid-cols-2" data-testid="monitor-form-settings-grid">
          <FormField
            control={form.control}
            name="interval"
            render={({ field }) => (
              <FormItem data-testid="monitor-form-interval-item">
                <FormLabel data-testid="monitor-form-interval-label">Check Interval</FormLabel>
                <Select
                  onValueChange={(value) => field.onChange(parseInt(value))}
                  defaultValue={field.value.toString()}
                >
                  <FormControl>
                    <SelectTrigger data-testid="monitor-form-interval-select-trigger">
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent data-testid="monitor-form-interval-select-content">
                    {INTERVAL_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value} data-testid={`monitor-form-interval-option-${option.value}`}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormDescription data-testid="monitor-form-interval-description">
                  How often to check the endpoint
                </FormDescription>
                <FormMessage data-testid="monitor-form-interval-error" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="method"
            render={({ field }) => (
              <FormItem data-testid="monitor-form-method-item">
                <FormLabel data-testid="monitor-form-method-label">HTTP Method</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger data-testid="monitor-form-method-select-trigger">
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent data-testid="monitor-form-method-select-content">
                    <SelectItem value="GET" data-testid="monitor-form-method-option-GET">GET</SelectItem>
                    <SelectItem value="POST" data-testid="monitor-form-method-option-POST">POST</SelectItem>
                    <SelectItem value="PUT" data-testid="monitor-form-method-option-PUT">PUT</SelectItem>
                    <SelectItem value="PATCH" data-testid="monitor-form-method-option-PATCH">PATCH</SelectItem>
                    <SelectItem value="DELETE" data-testid="monitor-form-method-option-DELETE">DELETE</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage data-testid="monitor-form-method-error" />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="alertStatusCodes"
          render={() => (
            <FormItem data-testid="monitor-form-alert-codes-item">
              <div className="mb-4">
                <FormLabel data-testid="monitor-form-alert-codes-label">Alert on Status Codes</FormLabel>
                <FormDescription data-testid="monitor-form-alert-codes-description">
                  Select which HTTP status codes should trigger alerts
                </FormDescription>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="monitor-form-alert-codes-grid">
                {COMMON_STATUS_CODES.map((status) => (
                  <FormField
                    key={status.code}
                    control={form.control}
                    name="alertStatusCodes"
                    render={({ field }) => {
                      return (
                        <FormItem
                          key={status.code}
                          className="flex items-center gap-2 space-y-0"
                          data-testid={`monitor-form-alert-code-item-${status.code}`}
                        >
                          <FormControl>
                            <Checkbox
                              checked={field.value?.includes(status.code)}
                              onCheckedChange={(checked) => {
                                return checked
                                  ? field.onChange([...field.value, status.code])
                                  : field.onChange(
                                    field.value?.filter(
                                      (value) => value !== status.code
                                    )
                                  );
                              }}
                              data-testid={`monitor-form-alert-checkbox-${status.code}`}
                            />
                          </FormControl>
                          <FormLabel className="text-sm font-normal cursor-pointer" data-testid={`monitor-form-alert-label-${status.code}`}>
                            {status.label}
                          </FormLabel>
                        </FormItem>
                      );
                    }}
                  />
                ))}
              </div>
              <FormMessage data-testid="monitor-form-alert-codes-error" />
            </FormItem>
          )}
        />

        <div className="flex items-center justify-between py-3 border-t" data-testid="monitor-form-advanced-section">
          <div className="space-y-0.5">
            <div className="text-sm font-medium" data-testid="monitor-form-advanced-title">Advanced Options</div>
            <div className="text-xs text-muted-foreground" data-testid="monitor-form-advanced-description">
              Add custom headers or request body
            </div>
          </div>
          <Switch
            checked={showAdvanced}
            onCheckedChange={setShowAdvanced}
            data-testid="monitor-form-advanced-switch"
          />
        </div>

        {showAdvanced && (
          <div className="space-y-6 pt-2" data-testid="monitor-form-advanced-content">
            <FormField
              control={form.control}
              name="headers"
              render={({ field }) => (
                <FormItem data-testid="monitor-form-headers-item">
                  <FormLabel data-testid="monitor-form-headers-label">Custom Headers (JSON)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder='{"Authorization": "Bearer token", "Content-Type": "application/json"}'
                      className="font-mono text-sm resize-none"
                      rows={4}
                      {...field}
                      data-testid="monitor-form-headers-input"
                    />
                  </FormControl>
                  <FormDescription data-testid="monitor-form-headers-description">
                    Optional: Add authentication headers or API keys as JSON
                  </FormDescription>
                  <FormMessage data-testid="monitor-form-headers-error" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="body"
              render={({ field }) => (
                <FormItem data-testid="monitor-form-body-item">
                  <FormLabel data-testid="monitor-form-body-label">Request Body</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder='{"key": "value"}'
                      className="font-mono text-sm resize-none"
                      rows={4}
                      {...field}
                      data-testid="monitor-form-body-input"
                    />
                  </FormControl>
                  <FormDescription data-testid="monitor-form-body-description">
                    Optional: Request body for POST/PUT/PATCH requests
                  </FormDescription>
                  <FormMessage data-testid="monitor-form-body-error" />
                </FormItem>
              )}
            />
          </div>
        )}

        <FormField
          control={form.control}
          name="isActive"
          render={({ field }) => (
            <FormItem className="flex items-center justify-between border rounded-md p-4" data-testid="monitor-form-active-item">
              <div className="space-y-0.5">
                <FormLabel data-testid="monitor-form-active-label">Start Monitoring Immediately</FormLabel>
                <FormDescription data-testid="monitor-form-active-description">
                  Begin monitoring this endpoint right away
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  data-testid="monitor-form-active-switch"
                />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="flex items-center gap-3 pt-4" data-testid="monitor-form-actions">
          <Button
            type="submit"
            disabled={isPending}
            className="flex-1"
            data-testid="monitor-form-submit-button"
          >
            {isPending ? "Saving..." : submitLabel}
          </Button>
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              data-testid="monitor-form-cancel-button"
            >
              Cancel
            </Button>
          )}
        </div>
      </form>
    </Form>
  );
}
